import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlaneUp, faCheck, faChevronRight, faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { useOffline } from '../../offline/OfflineContext';
import './OfflineDashboardStrip.css';

function relative(ms) {
  if (ms == null) return '';
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'moments ago';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/**
 * Dashboard entry point for offline access.
 *
 * A strip above the module grid rather than a seventh module card: offline is
 * a *mode*, not a module, and dropping it into the grid would read as a
 * product area the user might not have access to — the grid already uses that
 * visual language for locked modules.
 */
const OfflineDashboardStrip = () => {
  const {
    supported, packLive, pack, staleness, openPanel, offline,
    packIncomplete, failedSections,
  } = useOffline();

  if (!supported) return null;

  // An incomplete pack must not wear the green tick. This is the most
  // glanced-at surface in the whole feature — if it says "ready" while a third
  // of the sections are missing, nobody ever opens the panel to find out.
  const ready = packLive && !packIncomplete;

  let title;
  let sub;
  if (packIncomplete) {
    title = 'Offline copy is incomplete';
    sub = `${failedSections} section${failedSections === 1 ? '' : 's'} missing · saved ${relative(staleness)}`;
  } else if (packLive) {
    title = 'Ready to work offline';
    sub = `Saved ${relative(staleness)} · ${pack.responseCount} screens, ${pack.fileCount} documents`;
  } else {
    title = 'Flying soon? Take IMT with you';
    sub = 'Save a read-only copy of your data before you lose signal';
  }

  return (
    <button
      type="button"
      className={`hms-offline-strip ${ready ? 'is-ready' : ''} ${packIncomplete ? 'is-incomplete' : ''}`}
      onClick={openPanel}
    >
      <span className="hms-offline-strip-icon">
        <FontAwesomeIcon
          icon={packIncomplete ? faTriangleExclamation : ready ? faCheck : faPlaneUp}
        />
      </span>

      <span className="hms-offline-strip-text">
        <span className="hms-offline-strip-title">{title}</span>
        <span className="hms-offline-strip-sub">{sub}</span>
      </span>

      <span className="hms-offline-strip-cta">
        {offline ? 'Offline now' : packIncomplete ? 'Fix' : packLive ? 'Manage' : 'Set up'}
        <FontAwesomeIcon icon={faChevronRight} />
      </span>
    </button>
  );
};

export default OfflineDashboardStrip;
